import { useState, useCallback, RefObject } from 'react'
import { toPng } from 'html-to-image'
import { toast } from 'sonner'
import { useExport } from '@/hooks/useExport'
import { sanitizeFilename } from '@/lib/imageUtils'
import type { Note } from '@/types'
import type { ExportNote, ExportOptions } from '@/lib/export'

export interface ExportConfig extends ExportOptions {
  textFormat: 'txt' | 'md'
  includeText: boolean
  includeImages: boolean
  includeShareImage: boolean
}

export const DEFAULT_EXPORT_CONFIG: ExportConfig = {
  includeText: true,
  includeImages: true,
  includeShareImage: false,
  textFormat: 'txt',
  folderPerNote: true,
}

interface UseBulkExportOptions {
  shareCardRef: RefObject<HTMLDivElement | null>
  notes: Note[]
}

export function useBulkExport({ shareCardRef, notes }: UseBulkExportOptions) {
  const { exportNotes, isExporting } = useExport()
  const [shareCardNote, setShareCardNote] = useState<Note | null>(null)

  const captureShareImage = useCallback(async (note: Note): Promise<string> => {
    return new Promise((resolve, reject) => {
      setShareCardNote(note)
      // 给一点时间让 ShareCard 渲染新的 Note 内容
      setTimeout(async () => {
        if (!shareCardRef.current) {
          reject(new Error('分享卡片未渲染'))
          return
        }
        try {
          const dataUrl = await toPng(shareCardRef.current, { cacheBust: true, pixelRatio: 2 })
          resolve(dataUrl)
        } catch (error) {
          reject(error)
        }
      }, 100)
    })
  }, [shareCardRef])

  const handleExport = useCallback(async (selectedIds: string[], config: ExportConfig = DEFAULT_EXPORT_CONFIG) => {
    if (selectedIds.length === 0) {
      toast.error('请先选择笔记')
      return
    }
    
    const targetNotes = notes.filter((n) => selectedIds.includes(n.id))
    if (!targetNotes.length) {
      toast.error('没有可导出的笔记')
      return
    }

    try {
      const exportPayload: ExportNote[] = []
      
      for (const note of targetNotes) {
        const images = note.images
          .filter((img) => img.url && img.status === 'completed')
          .map((img, index) => ({
            url: img.url!,
            filename: `图片_${String(index + 1).padStart(2, '0')}.jpg`,
          }))

        const shareImageDataUrl = config.includeShareImage ? await captureShareImage(note) : undefined

        exportPayload.push({
          note,
          images,
          shareImageDataUrl,
          shareImageFilename: `${sanitizeFilename(note.title || '笔记')}_分享图.png`,
        })
      }

      await exportNotes(exportPayload, config)
      toast.success(`已导出 ${targetNotes.length} 篇笔记`)
    } catch (error) {
      console.error('[批量导出] 失败:', error)
      toast.error('导出过程发生错误')
    } finally {
      setShareCardNote(null)
    }
  }, [notes, captureShareImage, exportNotes])

  return {
    handleExport,
    isExporting,
    shareCardNote,
  }
}

